import React, { useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useHistory, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Grid, Header, Item, Message, Segment } from 'semantic-ui-react'
import { PATH_PRODUCTS_EDIT } from '../../../consts'
import { useQuery } from '../../../hooks/useQuery'
import { handleGenericErrors } from '../../../utils/Products/errorHandler'
import { AliasRemapControls } from './AliasRemapControls'
import { DuplicateAlias } from './DuplicateAlias'
import { PRODUCT_NOT_FOUND_ERROR, ALIAS_MISMATCH_ERROR } from './hooks/errors'
import { useFromProduct } from './hooks/useFromProduct'
import { useToProduct } from './hooks/useToProduct'
import { ProductDetails } from './ProductDetails'
import { ProductSearch } from './ProductSearch'

interface MessageType {
  error?: boolean
  positive?: true
  content?: string
  header?: string
}

export const ProductAliasRemap = () => {
  const history = useHistory()
  const query = useQuery()
  const aliasId = query.get('aliasId')
  const returnUrl = query.get('returnUrl')
  const [toProductId, setToProductId] = useState<string>('')
  const { productId: fromProductId } = useParams<{
    productId: string
  }>()

  const {
    product: fromProduct,
    error: fromError,
    errorCode: fromErrorCode,
    isLoading: isFromProductLoading,
    alias,
  } = useFromProduct(fromProductId, aliasId)

  const {
    product: toProduct,
    error: toError,
    errorCode: toErrorCode,
    isLoading: isToProductLoading,
  } = useToProduct(toProductId)

  const pageTitle = alias ? `Remap Alias #${aliasId}` : 'Remap Alias'
  const headingText = alias ? `Remap Alias: ${alias.name}` : 'Remap Alias'

  const [fromProductMessage, setFromProductMessage] = useState<MessageType | undefined>()
  const [toProductMessage, setToProductMessage] = useState<MessageType | undefined>()

  const navigateToReturnUrl = () => {
    const pathName = returnUrl || PATH_PRODUCTS_EDIT
    history.push(pathName)
  }

  const duplicateAlias = useMemo(() => {
    if (toProduct === null || fromProduct === null || alias === null) {
      return
    }

    const duplicate = toProduct?.aliases.find((item) => item.name === alias.name)

    return duplicate
  }, [toProduct, fromProduct, alias])

  useEffect(() => {
    if (fromError) {
      let messageText

      if (fromErrorCode === PRODUCT_NOT_FOUND_ERROR) {
        messageText = `Product #${fromProductId} was not found.`
      }

      handleGenericErrors(fromError, messageText)
    }

    if (fromErrorCode === PRODUCT_NOT_FOUND_ERROR) {
      setFromProductMessage({
        header: `Product #${fromProductId} was not found.`,
        error: true,
        content: 'Go back and try a different product',
      })

      return
    }

    if (fromErrorCode === ALIAS_MISMATCH_ERROR) {
      const messageText = `Alias #${aliasId} was not found on Product #${fromProductId}`

      toast(messageText, {
        containerId: 'error',
      })

      setFromProductMessage({
        header: messageText,
        error: true,
        content: 'Go back to product and try another alias.',
      })

      return
    }

    setFromProductMessage(undefined)
  }, [fromError, fromErrorCode, aliasId, fromProductId])

  // Display errors with toProduct
  useEffect(() => {
    if (toError) {
      let messageText

      if (toErrorCode === PRODUCT_NOT_FOUND_ERROR) {
        messageText = `Product #${toProductId} was not found.`
      }

      handleGenericErrors(toError, messageText)
    }

    if (toErrorCode === PRODUCT_NOT_FOUND_ERROR) {
      setToProductMessage({
        header: `Product #${toProductId} was not found.`,
        error: true,
        content: 'Search for a different product ID.',
      })

      return
    }

    setToProductMessage(undefined)
  }, [toError, toErrorCode, toProductId])

  // Reset toProduct when fromProduct changes
  useEffect(() => {
    setToProductId('')
  }, [fromProduct])

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>
      <Segment padded attached style={{ flex: '0 0 auto' }}>
        <Header as="h2">{headingText}</Header>
      </Segment>
      <Segment attached style={{ flex: '1 1 auto', display: 'flex' }}>
        <Grid style={{ flex: '1 1 auto' }} stackable>
          <Grid.Column width={8} style={{ display: 'flex', flexDirection: 'column' }}>
            <Header attached="top" as="h3" block>
              Remove From...
            </Header>
            <Segment
              attached
              loading={isFromProductLoading}
              style={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto' }}
            >
              <Item.Group>
                <Header size="large" as="h4">{`Product ID: ${fromProductId}`}</Header>
              </Item.Group>
              {fromProductMessage && (
                <Message {...fromProductMessage} style={{ flex: '0 0 auto' }} />
              )}
              {fromProduct && <ProductDetails product={fromProduct} />}
            </Segment>
          </Grid.Column>
          <Grid.Column width={8} style={{ display: 'flex', flexDirection: 'column' }}>
            <Header attached="top" as="h3" block>
              Remap To...
            </Header>
            <Segment
              attached
              loading={isToProductLoading}
              style={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto' }}
            >
              <ProductSearch setProductId={setToProductId} key={fromProductId} />
              <DuplicateAlias duplicateAlias={duplicateAlias} />
              {toProductMessage && <Message {...toProductMessage} style={{ flex: '0 0 auto' }} />}
              {toProduct && <ProductDetails product={toProduct} />}
            </Segment>
          </Grid.Column>
        </Grid>
      </Segment>
      <Segment attached style={{ flex: '0 0 auto' }}>
        <AliasRemapControls
          alias={alias}
          fromProduct={fromProduct}
          toProduct={toProduct}
          onCancel={navigateToReturnUrl}
        />
      </Segment>
    </>
  )
}
